import { inventoryFinalPosition } from '../../inventory-position';
import { InventoryWebsiteVisibilityComponent } from '../../inventory-website-visibility.component';
import { pricePerPound } from '../../price-units';
import { handlingBreakdown } from '../shared/purchase-order-costs';
import { cloneDraft, sameInventory } from '../shared/purchase-order-state';
import { PurchaseOrderLookupsService } from '../shared/purchase-order-lookups.service';
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { RmsModule } from '@rms';
import { NgForm } from '@angular/forms';
import { Inventory } from '@interfaces';
import { WebService, lastValueFrom } from '@services';

import { CalcFormComponent, CalcData } from '../calc-form/calc-form.component'

@Component({
  selector: 'app-inventory-form',
  imports: [InventoryWebsiteVisibilityComponent, RmsModule, CalcFormComponent],
  templateUrl: './inventory-form.component.html',
  styleUrl: './inventory-form.component.scss'
})
export class InventoryFormComponent implements OnInit, OnDestroy {
  // ---- Inputs / Outputs -----------------------------------------------------
  @Input() SelectedInventory: any | null = null;
  @Input() Pdf = false;
  @Input() saving = false;
  loading = true;
  loadError = '';
  calculationError = '';
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private schedule(work: () => void, delay = 0): ReturnType<typeof setTimeout> {
    const timer = setTimeout(() => { this.timers.delete(timer); work(); }, delay);
    this.timers.add(timer);
    return timer;
  }
  ngOnDestroy() { this.timers.forEach(clearTimeout); }

  @Input() DefaultValues: any | undefined;
  @Input() OfferSample: any = null;

  @Output() onLeaveInventoryForm = new EventEmitter<void>();
  @Output() onSaveInventoryForm = new EventEmitter<any>();
  @Output() onUpdateInventoryForm = new EventEmitter<any>();

  filteredPricings: any[] = [];

  // ---- State ----------------------------------------------------------------
  Inventory: any = {};
  InventoryData: any = {};

  showTotals = false;
  showDecafTotals = false;
  closingTotals = false;
  Updating = false;
  calcModalData : any | null = null
  // ---- Pricing inputs/display -----------------------------------------------
  PerPoundCost = '$0.0000';
  Price: number | null = null;            // UI price input (varies by descriptor)
  Diff: number | null = null;             // UI diff input (for BuyingTermID = 2)
  perPoundCostValue: number | null = 0;   // normalized $/lb
  currentPriceBase: number | null = null;
  shipDate: Date | null = null;

  // ---- Handling / fees ------------------------------------------------------
  readonly DEFAULT_EX_WHSE = 0.00; // <- default when needed
  WarehouseExWhseCost: number = 0.00;
  DaysToFinanceList = [
    { name: '30 Days', value: 30 },
    { name: '60 Days', value: 60 },
    { name: '90 Days', value: 90 },
  ];
  DaysToFinance = 30;

  steamShipRate = 0;
  marineInsurance = 0.17; // percent-of-value (0.17% => 0.0017)
  dutyAndEntry = 0;
  drayage = 0;
  stripping = 0;
  storage = 0;
  weighing = 0;
  financing = 0; // annual percent from destination port
  fot = 0;
  sampleCharge = 0;
  rfa = 0;
  rateBuffer = 0;
  finalCostWith = 0.015; // RNY buffer
  totalEstimate = 0;
  TotalCommissions = 0;

  // ---- Per-pound breakdown --------------------------------------------------
  netWeight = 0;
  grossWeight = 0;
  cost = 0;
  totalCost = 0;
  steamShipRatePerPound = 0;
  marineInsurancePerPound = 0;
  dutyAndEntryPerPound = 0;
  drayagePerPound = 0;
  strippingPerPound = 0;
  storagePerPound = 0;
  weighingPerPound = 0;
  financingPerPound = 0;
  fotPerPound = 0;
  sampleChargePerPound = 0;
  rfaPerPound = 0;
  totalBags = 0;

  // ---- Decaf ---------------------------------------------------------------
  DecafCostPerPound = 0.76;
  DecafCostTotal = 0;
  decafSeaforth = 800;
  decafStripping = 0;
  decafStrippingPerBag = 1.10;
  decafStorage = 0;
  decafStoragePerBag = 0.79;
  decafWeighing = 0;
  decafWeighingPerBag = 0.63;
  decafLossValue = 6.25;
  decafLossValuePercent = 6.25;
  totalDecafLossValue = 0;
  decafFinancing = 0;
  decafFinancingPercent = 10.00;
  totalDecafNetWeight = 0;
  totalDecafNetWeightFromLoss = 0;
  decafBuffer = 0.03;
  decafTolling = 0;
  decafTollingPerPound = 0;
  totalOrderWithDecafCost = 0;
  decafTrucking = 0;
  tollingCost = 0;
  _tollingCost: number | null = null

  // ---- User override for FOB->Ex Whse --------------------------------------
  private _fobToExWhseOverride: number | null = null;
  private hasUserOverriddenFob = false;
  private costOverrides: any = {};

  constructor(private lookups: PurchaseOrderLookupsService, private web: WebService) { }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================
  async ngOnInit() {
    this.loading = true;
    this.loadError = '';
    try {
      this.InventoryData = await this.getInventoryData();
      this.InventoryData.DecafProducts = (this.InventoryData.Products || []).filter((p: any) => p.Decaf === true);
      if (this.SelectedInventory) {
        this.Inventory = cloneDraft(this.SelectedInventory);
        this.Inventory.FixationCurrentLevel ??= this.Inventory.FixationBasis;
        this.Updating = !!this.Inventory.InventoryID;
        await this.setDefaults(false);
      } else if (this.OfferSample) {
        this.Inventory = { InventoryTypeID: this.OfferSample.InventoryTypeID };
        await this.setDefaults(false);
      }
      this.InventoryData.OpenRiskTickets = this.Inventory.RiskTicketID ? [{TicketNumber:this.Inventory.RiskTicketID, Label:String(this.Inventory.RiskTicketID)}] : [];
      const contract = this.Inventory.Child?.DecafTollingContractID;
      this.InventoryData.TollingContracts = contract ? [{TollingContractID:contract, Label:String(contract)}] : [];
    } catch (error) {
      this.loadError = 'Unable to load inventory details. Please retry.';
    } finally { this.loading = false; }
  }

  // ===========================================================================
  // Data Bootstrap
  // ===========================================================================
  private async getInventoryData() {
    const products$ = this.lookups.get('inventory', 'coffee/products');
    const paymentTerms$ = this.lookups.get('data', 'coffee/payment-terms');
    const sampleApprovalTerms$ = this.lookups.get('data', 'coffee/sample-approval-terms');
    const weightTypes$ = this.lookups.get('data', 'coffee/weight-types');
    const shipmentTerms$ = this.lookups.get('data', 'coffee/shipment-terms');
    const shipmentTypes$ = this.lookups.get('data', 'coffee/shipment-types');
    const futuresMonths$ = this.lookups.get('inventory', 'coffee/risk-board/futures');
    const destinationWarehouses$ = this.lookups.get('data', 'coffee/locations');
    const destinationPorts$ = this.lookups.get('data', 'coffee/destinations');
    const priceDescriptors$ = this.lookups.get('data', 'coffee/price-descriptors');
    const originRates$ = this.lookups.get('data', 'coffee/origin-rates');
    const costings$ = this.lookups.get('data', 'coffee/costings');
    const decaffeinators$ = this.lookups.get('data', 'coffee/decaffeinators');

    const [
      Products,
      PaymentTerms,
      SampleApprovalTerms,
      WeightTypes,
      ShipmentTerms,
      ShipmentTypes,
      FuturesMonths,
      DestinationWarehouses,
      DestinationPorts,
      PriceDescriptors,
      OriginRates,
      Costings,
      Decaffeinators
    ] = await Promise.all([
      lastValueFrom(products$),
      lastValueFrom(paymentTerms$),
      lastValueFrom(sampleApprovalTerms$),
      lastValueFrom(weightTypes$),
      lastValueFrom(shipmentTerms$),
      lastValueFrom(shipmentTypes$),
      lastValueFrom(futuresMonths$),
      lastValueFrom(destinationWarehouses$),
      lastValueFrom(destinationPorts$),
      lastValueFrom(priceDescriptors$),
      lastValueFrom(originRates$),
      lastValueFrom(costings$),
      lastValueFrom(decaffeinators$),
    ]);

    return {
      AllFuturesMonths: FuturesMonths,
      Products,
      PaymentTerms,
      SampleApprovalTerms,
      WeightTypes,
      ShipmentTerms,
      ShipmentTypes,
      FuturesMonths,
      DestinationWarehouses,
      DestinationPorts,
      PriceDescriptors,
      OriginRates,
      Costings,
      Decaffeinators,
      Months: [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
      ],
      Years: this.getYears(),
      OpenRiskTickets: [],
      TollingContracts: []
    };
  }

  private getYears() {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const year = currentYear + i;
      return { label: year.toString(), value: year };
    });
  }

  // ===========================================================================
  // Defaults & Dependent Data
  // ===========================================================================
  async setDefaults(productChanged = true): Promise<void> {
      const savedFob = this.Inventory.FOBtoExWarehouseValueTemp;
      const savedExWhse = this.Inventory.ExWhseCost;
      if (productChanged) this.costOverrides = {};
      const selectedProduct = (this.InventoryData?.Products || [])
        .find((p: any) => p.InventoryTypeID == this.Inventory?.InventoryTypeID);
      if (!selectedProduct) return;

      // seed model from product
      this.Inventory.DecafProduct = selectedProduct.Decaf;
      this.Inventory.NetWeight = productChanged ? selectedProduct.WeightDefaultValue : (this.Inventory.NetWeight ?? selectedProduct.WeightDefaultValue);
      this.Inventory.WeightTypeID = this.Inventory.WeightTypeID ?? selectedProduct.WeightPurchaseSku;
      this.Inventory.FairTrade = selectedProduct.FairTrade;
      this.Inventory.DateFixed = this.Inventory.DateFixed ?? new Date();
      this.Inventory.Country = selectedProduct.Country;
      this.Inventory.ExchangeKey = selectedProduct.FuturesExchangeKey;
      this.Inventory.InventoryTypeName = selectedProduct.InventoryTypeName;
      this.Inventory.WebInvisible = this.Inventory.WebInvisible ?? false;
      if (this.Inventory.PriceFixedBy == null && this.Inventory.PriceToBeFixedByRoyal != null) {
        this.Inventory.PriceFixedBy = this.Inventory.PriceToBeFixedByRoyal ? 1 : 2;
      }
      if (this.Inventory.IsHedged == null && this.Inventory.Hedged != null) {
        this.Inventory.IsHedged = this.Inventory.Hedged ? 1 : 2;
      }
      this.DaysToFinance = selectedProduct.DaysToFinance ?? 30;
      if (selectedProduct.FairTrade) {
        this.filteredPricings = this.InventoryData.PriceDescriptors.filter((pd: any) => pd.PriceDescriptorID != 2);
      }
      else {
        this.filteredPricings = this.InventoryData.PriceDescriptors;
      }
      // filter futures by exchange
      this.InventoryData.FuturesMonths =
        (this.InventoryData.AllFuturesMonths || []).filter((fm: any) => String(fm.Exchange) === String(this.Inventory.ExchangeKey));

      // seed price/diff
      this.Price = this.Inventory.PricePerUnit ?? undefined;
      this.Diff = this.Inventory.DifferentialPerUnit ?? undefined;

      if (this.Price != null || this.Diff != null) {
        this.updatePerPoundCost();
      }

      // defaults from parent list, if provided
      if (this.DefaultValues && !this.SelectedInventory) {
        console.log(this.DefaultValues);
        const v = this.DefaultValues;
        this.Inventory.ETDMonth = v.ETDMonth;
        this.Inventory.ETDYear = v.ETDYear;
        this.Inventory.ShippingTerm = v.ShippingTerm;
        this.Inventory.Destination = v.Destination;
        this.Inventory.PaymentTermsID = v.PaymentTermsID;
        this.Inventory.ShipmentTypeID = v.ShipmentTypeID;
        this.Inventory.FutureCompositeID = v.FutureCompositeID;
        this.Inventory.FixationCurrentLevel = v.FixationCurrentLevel ?? v.FixationBasis;
        this.Inventory.PurchaseOrderInventories = v.PurchaseOrderInventories;
        this.updateWarehouses();
        this.Inventory.FinalDestinationWarehouseID = v.FinalDestinationWarehouseID;
      }

      // normalize year -> shipDate
      if (this.Inventory.ETDYear && this.Inventory.ETDMonth) {
        let etdYear = Number(this.Inventory.ETDYear);
        if (etdYear < 100) etdYear = 2000 + etdYear;
        this.Inventory.ETDYear = etdYear;
        this.shipDate = new Date(etdYear, this.Inventory.ETDMonth - 1, 1);
      }

      // fetch base + initial calc for existing rows
      if (this.Inventory.InventoryID && this.Inventory.ExchangeKey) {
        this.currentPriceBase = await this.getCurrentPriceBase();
        this.Inventory.PriceFixedBy = this.Inventory.PriceToBeFixedByRoyal ? 1 : 2;
        this.Inventory.IsHedged = this.Inventory.Hedged ? 1 : 2;
        this.updatePerPoundCost();
      }

      // Apply shipping rules then recalc
      if (productChanged || !this.Inventory.FutureCompositeID) this.setClosestFuturesMonth();
      this.applyShippingRules();     // sets ExWhse to 0 when ShippingTerm = 4; clears overrides, etc.
      this.recalculateCostsDebounced();
    this.Inventory.PurchaseOrderInventories = cloneDraft(this.DefaultValues?.PurchaseOrderInventories ?? this.Inventory.PurchaseOrderInventories ?? []);
    this.updateWarehouses();
    if (!productChanged && savedExWhse != null) this.Inventory.ExWhseCost = savedExWhse;
    if (!productChanged && savedFob != null && this.Inventory.ShipmentTypeID === 1 && this.Inventory.ShippingTerm !== 4) {
      this._fobToExWhseOverride = Number(savedFob);
      this.Inventory.FOBtoExWarehouseValueTemp = Number(savedFob);
      this.hasUserOverriddenFob = true;
    }
  }

  updateWarehouses() {
    const rows = (this.InventoryData.DestinationWarehouses || []).filter((dw: any) => dw.DestinationId == this.Inventory.Destination);
    this.InventoryData.DestinationWarehousesFiltered = rows;
    if (!rows.some((row: any) => row.LocationID == this.Inventory.FinalDestinationWarehouseID)) {
      const defaults: any = { 1: 37, 17: 45, 16: 31, 15: 23, 10: 46 };
      this.Inventory.FinalDestinationWarehouseID = rows.find((row: any) => row.LocationID == defaults[this.Inventory.Destination])?.LocationID ?? null;
    }
    this.Inventory.Decaf = [10,15].includes(Number(this.Inventory.Destination)) && !this.Inventory.DecafProduct;
    this.onWarehouseChanged(this.Inventory.FinalDestinationWarehouseID);
  }

  // ===========================================================================
  // Events from Template (wire these where needed)
  // ===========================================================================
  onBagsOrMarksChanged() {
    this.recalculateCostsDebounced();
  }

  onMonthChange(selectedMonth: number) {
    this.Inventory.ETDMonth = Number(selectedMonth);
    this.updateValidYearsForMonth(selectedMonth);
    this.setClosestFuturesMonth();
    this.recalculateCostsDebounced();
  }

  onYearChange() {
    this.recalculateCostsDebounced();
    this.setClosestFuturesMonth();
  }

  onDestinationChanged() {
    console.log(this.Inventory)
    if (this.Inventory.Decaf && this.Inventory.Child) {
      if (this.Inventory.Destination != 10 && this.Inventory.Destination != 15) {
        const proceed = confirm(
          'Final Destination Warehouse is not a decaf location.\n\n' +
          'If you continue, the decaf child inventory will be deleted.\n\nProceed?'
        );
        if (!proceed) return;
        (this.Inventory as any).__deleteChild = true;
        (this.Inventory as any).__deleteChildId = this.Inventory.Child.InventoryID;
        delete this.Inventory.Child;
        this.Inventory.Decaf = false;
        delete this.Inventory.DecafID;
      }
      if (this.Inventory.Destination == 10 || this.Inventory.Destination == 15) {
        if (this.Inventory.Destination == 15) this.Inventory.Child.DecaffeinatorID = 1992;
        if (this.Inventory.Destination == 10) this.Inventory.Child.DecaffeinatorID = 6815;
      }
    }
    this.recalculateCostsDebounced();
    this.updateWarehouses();
  }
  
  onWarehouseChanged(id: number) {
    this.Inventory.FinalPosition = inventoryFinalPosition({ ...this.Inventory, FinalDestinationWarehouseID: id }, this.InventoryData?.DestinationWarehouses, this.InventoryData?.ShipmentTerms);
    this.WarehouseExWhseCost = Number(this.InventoryData?.DestinationWarehouses?.find((w: any) => w.LocationID == id)?.LocationExWarehouseCost ?? 0);
    if (this.Inventory.ShipmentTypeID) {
      this.onShipmentTypeChange(this.Inventory.ShipmentTypeID);
    }
    else {
      this.recalculateCostsDebounced();
    }
  }

  // Price-related changes must convert to $/lb and recalc
  onPriceChange(val: number | null) {
    this.Price = val;
    this.updatePerPoundCost();
    this.recalculateCostsDebounced();
  }
  onDiffChange(val: number | null) {
    this.Diff = val;
    this.updatePerPoundCost();
    this.recalculateCostsDebounced();
  }
  onPriceDescriptorChange() {
    this.updatePerPoundCost();
    this.recalculateCostsDebounced();
  }

  // Shipment/Shipping rules
  onShipmentTypeChange(newType: number) {
    this.Inventory.ShipmentTypeID = Number(newType);

    // Reset all handling fields & overrides whenever type changes
    this._fobToExWhseOverride = null;
    this.hasUserOverriddenFob = false;
    this.Inventory.FOBtoExWarehouseValueTemp = null;

    // Ensure Ex-Whse present for non-FOBEX modes
    if (this.Inventory.ShipmentTypeID === 2 && this.Inventory.ShippingTerm != 4) {
      this.Inventory.ExWhseCost = this.WarehouseExWhseCost;
    }

    // Respect Shipping Term rule (term can force further adjustments)
    if (this.Inventory.ShippingTerm === 4) {
      // No handling used in calc, but keep field visible & editable with value 0.00
      this.Inventory.ExWhseCost = this.DEFAULT_EX_WHSE;
    }

    this.recalculateCostsDebounced();
  }

  onShippingTermChange(newTerm: number) {
    this.Inventory.ShippingTerm = Number(newTerm);
    this.Inventory.FinalPosition = inventoryFinalPosition(this.Inventory, this.InventoryData?.DestinationWarehouses, this.InventoryData?.ShipmentTerms);

    // Reset overrides always when term changes
    this._fobToExWhseOverride = null;
    this.hasUserOverriddenFob = false;
    this.Inventory.FOBtoExWarehouseValueTemp = null;

    if (Number(newTerm) === 4) {
      // Set Ex-Whse to 0.00 (editable), calculations will ignore handling adder when term=4
      this.Inventory.ExWhseCost = this.DEFAULT_EX_WHSE;
    } else {
      // Reinstate a sane default if in Ex-Whse mode and field is empty
      if (this.Inventory.ShipmentTypeID == 2) {
        this.Inventory.ExWhseCost = this.WarehouseExWhseCost;
      }
    }

    this.recalculateCostsDebounced();
  }

  // ===========================================================================
  // Futures helpers
  // ===========================================================================
  updateValidYearsForMonth(selectedMonth: number) {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const existingYear = +this.Inventory.ETDYear;

    let minYear = (selectedMonth < currentMonth) ? currentYear + 1 : currentYear;
    if (existingYear && existingYear < minYear) minYear = existingYear;

    const yearsToShow = 6;
    const yearList = Array.from({ length: yearsToShow }, (_, i) => {
      const year = minYear + i;
      return { label: year.toString(), value: year };
    });

    if (existingYear && !yearList.find(y => y.value === existingYear)) {
      yearList.unshift({ label: existingYear.toString(), value: existingYear });
    }

    this.InventoryData.Years = yearList;
    const isValid = yearList.some(y => y.value === existingYear);
    if (!isValid && this.Inventory.InventoryID) {
      this.Inventory.ETDYear = minYear;
    }
  }

  setClosestFuturesMonth() {
    this.Inventory.FinalPosition = inventoryFinalPosition(this.Inventory, this.InventoryData?.DestinationWarehouses, this.InventoryData?.ShipmentTerms);
    const { ETDMonth, ETDYear } = this.Inventory;
    const futuresMonths = this.InventoryData?.FuturesMonths;
    if (!ETDMonth || !ETDYear) return;

    const year = Number(ETDYear) < 100 ? 2000 + Number(ETDYear) : Number(ETDYear);
    const etd = new Date(year, Number(ETDMonth) - 1, 1);
    this.shipDate = etd;

    const etdPlus45 = new Date(etd);
    etdPlus45.setDate(etdPlus45.getDate() + 45);

    const datedMonths = (futuresMonths || []).filter((m: any) => m.FND && Number.isFinite(new Date(m.FND).getTime()));
    if (!datedMonths.length) return;
    let closest = datedMonths[0];
    let minDiff = Math.abs(new Date(closest.FND).getTime() - etdPlus45.getTime());
    for (const m of datedMonths) {
      const diff = Math.abs(new Date(m.FND).getTime() - etdPlus45.getTime());
      if (diff < minDiff) { minDiff = diff; closest = m; }
    }

    this.Inventory.FutureCompositeID = closest.FutureCompositeID;
  }

  private formatToFuturesMonth(date: Date): string {
    const m = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][date.getMonth()];
    const y = date.getFullYear().toString().slice(-2);
    return `${m}'${y}`;
  }

  async getCurrentPriceBase(): Promise<number> {
    const basis = await lastValueFrom(
      this.web.getWebCall('data', 'coffee/price-basis', { params: [{ name: 'ExchangeKey', value: this.Inventory.ExchangeKey }] })
    );
    return basis?.length ? basis[0].PriceBase : 2.75;
  }

  // ===========================================================================
  // Risk / Flags
  // ===========================================================================
  SetPriceToBeFixedBy(inventory: Inventory) {
    inventory.PriceToBeFixedByRoyal = inventory.PriceFixedBy == 1;
    inventory.PriceToBeFixedByShipper = inventory.PriceFixedBy == 2;
  }
  SetHedged() {
    this.Inventory.Hedged = this.Inventory.IsHedged == 1;
  }

  // ===========================================================================
  // Price math
  // ===========================================================================
  updatePerPoundCost(): void {
    // Normalize to $/lb based on PriceDescriptorID and BuyingTermID
    if (this.Price == null && this.Diff == null) {
      this.perPoundCostValue = 0;
      this.PerPoundCost = '$0.0000';
      return;
    }

    let value: number | null = 0;
    const descriptor = String(this.Inventory.PriceDescriptorID);

    this.Inventory.PricePerUnit = this.Inventory.BuyingTermID == 2 ? null : this.Price;
    this.Inventory.DifferentialPerUnit = this.Inventory.BuyingTermID == 2 ? this.Diff : null;

    value = pricePerPound(this.Inventory.BuyingTermID == 2 ? this.Diff : this.Price, descriptor);
    this.Inventory.DifferentialPerPound = this.Inventory.BuyingTermID == 2 ? value : null;
    this.Inventory.PricePound = this.Inventory.BuyingTermID != 2 ? value : null;

    this.perPoundCostValue = value ?? 0;
    this.Inventory.FixationBasis = value;

    this.PerPoundCost = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4
    }).format(value || 0);
  }

  // ===========================================================================
  // Handling cost rules
  // ===========================================================================
  // Visible/used only when ShipmentTypeID === 1 (unless ShippingTerm === 4, which forces adder=0)
  get FOBtoExWarehouseValueTemp(): number {
    if (this._fobToExWhseOverride !== null) return this._fobToExWhseOverride;
    if (this.totalEstimate) return this.totalEstimate + this.rateBuffer + this.finalCostWith;
    return 0;
  }
  set FOBtoExWarehouseValueTemp(val: any) {
    if (val === null || val === undefined || val === '') {
      if (this.hasUserOverriddenFob) this.resetFobOverride();
      return;
    }
    this._fobToExWhseOverride = Number(val);
    this.Inventory.FOBtoExWarehouseValueTemp = this._fobToExWhseOverride;
    this.hasUserOverriddenFob = true;
    this.recalculateCostsDebounced();
  }
  private resetFobOverride() {
    this._fobToExWhseOverride = null;
    this.hasUserOverriddenFob = false;
    this.Inventory.FOBtoExWarehouseValueTemp = this.FOBtoExWarehouseValueTemp;
  }

  private getHandlingAdder(): number {

    const exwhse = this.Inventory.ExWhseCost ?? this.DEFAULT_EX_WHSE;
    // Shipping Term = 4 => no handling in calc (field still editable, starts at 0.00)
    if (this.Inventory?.ShippingTerm === 4) return exwhse;

    // ShipmentTypeID 1 => use FOBEX; else => use Ex-Whse
    if (this.Inventory?.ShipmentTypeID === 1) {
      return (this._fobToExWhseOverride ?? this.FOBtoExWarehouseValueTemp ?? 0);
    }
    return exwhse;
  }

  private applyShippingRules() {
    // Ensure consistent defaults on load
    if (this.Inventory.ShippingTerm === 4) {
      this.Inventory.ExWhseCost ??= this.DEFAULT_EX_WHSE;
      this.Inventory.FOBtoExWarehouseValueTemp = null;
      this._fobToExWhseOverride = null;
      this.hasUserOverriddenFob = false;
    } else if (this.Inventory.ShipmentTypeID !== 1) {
      // Ex-Whse mode needs a value (editable)
      if (this.Inventory.ExWhseCost == null) this.Inventory.ExWhseCost = this.DEFAULT_EX_WHSE;
      this.Inventory.FOBtoExWarehouseValueTemp = null;
    }
  }
  // ===========================================================================
  // Update Buying ID
  // ===========================================================================
  onUpdateBuyingTermID() {
    if (this.Inventory.BuyingTermID && !this.Inventory.InventoryID) {
      this.Inventory.IsHedged = this.Inventory.BuyingTermID == 2 ? 2 : null;
    }
    if (this.Inventory.BuyingTermID == 2) {
      this.Price = null; // reset Price when switching to Differential
      this.perPoundCostValue = 0; // reset per-pound cost
      this.PerPoundCost = '$0.0000'; // reset display
      this.Inventory.PricePerUnit = null; // reset PricePerUnit
      this.Inventory.PricePound = null; // reset PricePound
      this.updatePerPoundCost();
      this.recalculateCostsDebounced();
    }
    else if (this.Inventory.BuyingTermID == 1) {
      this.Diff = null; // reset Diff when switching to Price
      this.perPoundCostValue = 0; // reset per-pound cost
      this.PerPoundCost = '$0.0000'; // reset display
      this.Inventory.DifferentialPerUnit = null; // reset DifferentialPerUnit
      this.Inventory.DifferentialPerPound = null; // reset DifferentialPerPound
      this.updatePerPoundCost();
      this.recalculateCostsDebounced();
    }
  }
  // ===========================================================================
  // Main calc
  // ===========================================================================
  recalculateCostsDebounced() {
    clearTimeout((this as any)._costRecalcTimer);
    (this as any)._costRecalcTimer = this.schedule(() => { this.recalculateCosts(); if (this.Inventory.Child) this.recalculateDecafCosts() }, 80);
  }

  async recalculateCosts() {
    // Derive this from the current form, including inherited dates and reopened drafts.
    const year = Number(this.Inventory.ETDYear);
    this.shipDate = year && this.Inventory.ETDMonth
      ? new Date(year < 100 ? year + 2000 : year, Number(this.Inventory.ETDMonth) - 1, 1) : null;
    this.calculationError = '';
    this.UpdateTotalCommissions();
    // UI hedged default when creating (non-updating)

    // required data before calc
    if (!this.Inventory.Country || !this.Inventory.FinalDestinationWarehouseID || !this.Inventory.BagsOrdered ||
      !this.Inventory.NetWeight || !this.Inventory.Destination || !this.shipDate || this.Inventory.FixationCurrentLevel == null) {
      return 0;
    }
    await this.UpdateTotalCommissions();
    const today = new Date();
    const selectedPort = (this.InventoryData.DestinationPorts || [])
      .find((dp: any) => dp.destination_id == this.Inventory.Destination);

    // Rates by origin/destination/valid dates
    const availableRates = (this.InventoryData.OriginRates || [])
      .filter((or: any) => or.CountryID == this.Inventory.Country &&
        or.DestinationID == this.Inventory.FinalDestinationWarehouseID);

    let selectedRate: any; let buffer: number | undefined;

    if (availableRates?.length) {
      selectedRate = availableRates
        .filter((or: any) => new Date(or.DateFrom) <= this.shipDate! && new Date(or.DateGoodThru) >= this.shipDate!)
        .sort((a: any, b: any) => new Date(b.DateFrom).getTime() - new Date(a.DateFrom).getTime())[0];

      if (!selectedRate) {
        selectedRate = availableRates
          .filter((or: any) => new Date(or.DateFrom) <= today)
          .sort((a: any, b: any) => b.SteamshipRate - a.SteamshipRate)[0];
        buffer = selectedRate?.AddedBuffer;
        if (selectedRate) selectedRate = { ...selectedRate, WorstCase: true };
      } else {
        buffer = selectedRate?.Buffer;
      }
    }

    this.calculationError = !selectedRate && this.Inventory.ShipmentTypeID == 1 && this.Inventory.ShippingTerm != 4
      ? 'No applicable freight rate was found for this origin and destination. Check the rate setup before saving.' : '';
    if (this.calculationError) return;

    // normalize base per-pound price
    this.Inventory.PricePerUnit = this.Price ?? null;
    this.Inventory.PricePound = this.PerPoundCost;

    if (this.Inventory.BuyingTermID == 2) {
      this.Inventory.DifferentialPerUnit = this.Diff;
      this.Inventory.DifferentialPerPound = this.perPoundCostValue;
      this.Inventory.PricePerUnit = null;
      this.Inventory.PricePound = null;
    } else {
      this.Inventory.DifferentialPerUnit = null;
      this.Inventory.DifferentialPerPound = null;
      this.Inventory.PricePerUnit = this.Price;
      this.Inventory.PricePound = this.perPoundCostValue;
    }

    const adder = this.getHandlingAdder();
    const pricebase = this.Inventory.FixationCurrentLevel ?? this.currentPriceBase;
    // unit cost (per lb)
    this.cost = (this.Inventory.BuyingTermID == 2
      ? (pricebase ?? 0) + (this.Inventory.DifferentialPerPound ?? 0)
      : (this.Inventory.PricePound ?? 0)) + Number(this.TotalCommissions || 0);

    // fixed/port-driven costs
    this.steamShipRate = Number(this.costOverrides.steamShipRate ?? selectedRate?.SteamshipRate ?? 0);
    this.dutyAndEntry = Number(selectedPort?.duty_entry_fee ?? 0);
    this.drayage = Number(selectedPort?.drayage_cost ?? 0);
    this.stripping = Number(selectedPort?.container_unloading ?? 0);
    this.storage = Number(selectedPort?.storage_fee ?? 0);
    this.weighing = Number(selectedPort?.weight_verification ?? 0);
    this.financing = Number(selectedPort?.financial_charges ?? 0);
    this.sampleCharge = Number(selectedPort?.sample_inspection ?? 0);
    this.rateBuffer = Number(this.costOverrides.rateBuffer ?? buffer ?? 0);
    let netWeightTotal = 0;
    let grossWeightTotal = 0;
    let totalCostTotal = 0;
    let totalBags = 0;
    let perPoundCost = 0;

    // weights
    if (this.Inventory.PurchaseOrderInventories) {
      this.Inventory.PurchaseOrderInventories.filter((poi: any) => !sameInventory(poi, this.Inventory)).forEach((poi: any) => {
        netWeightTotal += Number(poi.BagsOrdered * poi.NetWeight);
        grossWeightTotal += Number(poi.BagsOrdered * (poi.NetWeight + 2));
        totalCostTotal += Number((poi.BagsOrdered * poi.NetWeight) * (poi.PricePound ?? (poi.DifferentialPerPound ? (poi.DifferentialPerPound + pricebase) : 0)));
        totalBags += Number(poi.BagsOrdered);
        perPoundCost += (poi.BuyingTermID == 2 ? (pricebase + (poi.DifferentialPerPound ?? 0)) : (poi.PricePound ?? 0));
      })
    }
    // Include the current draft once; sibling snapshots must not replace its edited quantity.
    const currentPounds = Number(this.Inventory.BagsOrdered) * Number(this.Inventory.NetWeight);
    this.netWeight = currentPounds + netWeightTotal;
    this.grossWeight = Number(this.Inventory.BagsOrdered) * (Number(this.Inventory.NetWeight) + 2) + grossWeightTotal;
    this.totalCost = this.cost * currentPounds + totalCostTotal;
    this.totalBags = Number(this.Inventory.BagsOrdered) + totalBags;
    this.cost = this.netWeight > 0 ? this.totalCost / this.netWeight : 0;
    if (!(this.netWeight > 0)) { this.calculationError = 'Enter a positive bag quantity and weight.'; return; }

    Object.assign(this, handlingBreakdown({ ...this, TariffInPrice: this.Inventory.TariffInPrice, TariffAmount: this.Inventory.TariffAmount }));
    this.Inventory.FOBtoExWarehouseValueTemp = this.Inventory.ShipmentTypeID == 1 && this.Inventory.ShippingTerm != 4
      ? this.FOBtoExWarehouseValueTemp : null;

    // Final fields shown in UI
    if (this.Inventory.BuyingTermID == 2) {
      this.Inventory.FinalWarehouseInCostTemp = null;
      this.Inventory.FinalWarehouseInCost = null;
      this.Inventory.FinalWarehouseInDifferential = this.getHandlingAdder() + ((this.Inventory.DifferentialPerPound ?? 0) + Number(this.TotalCommissions || 0));
      this.Inventory.EstimatedCost = this.getHandlingAdder() + (((this.Inventory.DifferentialPerPound ?? 0) + (pricebase ?? 0)) + Number(this.TotalCommissions || 0));
    } else {
      this.Inventory.FinalWarehouseInCost = this.getHandlingAdder() + ((this.Inventory.PricePound ?? 0) + Number(this.TotalCommissions || 0));
      console.log(this.getHandlingAdder());
      this.Inventory.FinalWarehouseInDifferential = null;
      this.Inventory.EstimatedCost = null;
    }
    this.Inventory.WorstCase = selectedRate?.WorstCase || false;
    return this.totalEstimate + this.rateBuffer + this.finalCostWith;
  }

  // ===========================================================================
  // Decaf
  // ===========================================================================
  setDecafDefaults(): void {
    const selectedProduct = (this.InventoryData?.DecafProducts || [])
      .find((p: any) => p.InventoryTypeID == this.Inventory?.DecafID);
    if (!selectedProduct || !this.shipDate) return;
    console.log(selectedProduct);
    const etdDate = new Date(this.shipDate);
    etdDate.setMonth(etdDate.getMonth() + 3);

    delete this.Inventory.DecafID;
    this.Inventory.Child = {
      ...structuredClone(this.Inventory),
      NetWeight: 132.28,
      ETDMonth: this.DecafMonthYear(etdDate.getMonth(), etdDate.getFullYear()).month,
      ETDYear: this.DecafMonthYear(etdDate.getMonth(), etdDate.getFullYear()).year,
      BagsOrdered: this.Inventory.BagsOrdered,
      ProductName: selectedProduct.InventoryTypeName,
      InventoryTypeID: selectedProduct.InventoryTypeID,
      Child: true,
      DecaffeinatorID: this.InventoryData.Decaffeinators.find((d: any) => d.AssociatedWarehouseComplexKey === this.Inventory.FinalDestinationWarehouseID)?.DecaffeinatorID || null,
      FinalDestinationWarehouseID: null,

    };
    this.updateDecafBags();
  }
DecafMonthYear(month: number, year: number): { month: number; year: number } {
  const m = Number(month), y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) throw new Error('Invalid month/year');

  // Accept 1–12 (domain style) or 0–11 (Date style)
  const zeroBased = (m >= 1 && m <= 12) ? m - 1 : m;

  const d = new Date(y, zeroBased, 1);
  d.setMonth(d.getMonth() + 1); // add 2 months

  return { month: d.getMonth(), year: d.getFullYear() }; // return 1–12 month
}
  onChangeTollingCost(tolling: number) {
    if (tolling != null) {
      this._tollingCost = tolling;
    }
    else {
      this._tollingCost = null;
    }
  }
  confirmFixationDebounced() {
    clearTimeout((this as any)._fixationTimer);
    (this as any)._fixationTimer = this.schedule(() => this.confirmFixation(), 150);
  }
  confirmFixation() {
    const raw = Number(this.Inventory?.FixationCurrentLevel);
    if (!Number.isFinite(raw)) return;

    if (raw > 10) {
      const fixed = this.normalizeOneDigitBeforeDecimal(raw);
      this.Inventory.FixationCurrentLevel = fixed;
      // optional: notify the user
      // this.snackBar?.open(`Auto-corrected fixation from ${raw} → ${fixed}`, 'OK', { duration: 3000 });
    }
  }
  /** Ensures exactly one digit before the decimal; caps to 4 fractional digits. */
  private normalizeOneDigitBeforeDecimal(n: number): number {
    if (n === 0) return 0;

    const sign = n < 0 ? -1 : 1;
    let v = Math.abs(n);

    if (v <= 10) {
      // Already fine; (optionally) cap to 4 decimals without adding trailing zeros:
      return sign * Number(v.toFixed(4));
    }

    // Shift decimal left so only one digit remains before the decimal.
    // e.g., 397 -> 3.97, 12345 -> 1.2345, 100 -> 1
    const pow = Math.floor(Math.log10(v));    // how many places to shift
    v = v / Math.pow(10, pow);

    // Up to 4 decimals (trim trailing zeros by casting back to Number)
    return sign * Number(v.toFixed(4));
  }
  recalculateDecafCostsDebounced() {
    clearTimeout((this as any)._decafTimer);
    (this as any)._decafTimer = this.schedule(() => this.recalculateDecafCosts(), 80);
  }

  updateDecafBags() {
    if (!this.Inventory.Child) return;

    const weghtPercent = this.Inventory.Child.DecaffeinatorID == 6815 ? 0.9375 : 0.95;

    const rawBags = Math.floor(((this.Inventory.BagsOrdered * this.Inventory.NetWeight) * weghtPercent) / 132.28);
    //const adjustedBags = Math.floor(rawBags * 0.95);
    this.Inventory.Child.BagsOrdered = rawBags;

    this.recalculateCostsDebounced();
  }

  async recalculateDecafCosts() {
    await this.UpdateTotalCommissions()

    const DecafInventory = this.Inventory.Child;
    this.updateDecafBags();
    if (!DecafInventory) return;
    if (!DecafInventory?.DecaffeinatorID || !DecafInventory.FinalDestinationWarehouseID || !DecafInventory.BagsOrdered) return;

    // --- user override path unchanged ---
    if (this._tollingCost != null) {
      this.tollingCost = this._tollingCost;
      if (this.Inventory.ShipmentTypeID === 1) {
        this.Inventory.Child.FOBtoExWarehouseValueTemp = this.tollingCost;
      } else {
        // this.Inventory.Child.ExWhseCost = this.tollingCost; // if needed
      }
      this.FOBtoExWarehouseValueTemp = 0.00;
      this._fobToExWhseOverride = 0.00;
      this.Inventory.ExWarehouseCost = 0.00;
      this.Inventory.FOBToExWarehouseValueTemp = 0.00;
      return;
    }

    // Keep child buying term mirroring...
    if (this.Inventory.BuyingTermID == 2) {
      this.Inventory.Child.BuyingTermID = 2;
      this.Inventory.Child.DifferentialPerUnit = this.Diff;
      this.Inventory.Child.DifferentialPerPound = this.perPoundCostValue;
      this.Inventory.Child.PricePerUnit = null;
      this.Inventory.Child.PricePound = null;
    } else {
      this.Inventory.Child.BuyingTermID = 1;
      this.Inventory.Child.DifferentialPerUnit = null;
      this.Inventory.Child.DifferentialPerPound = null;
      this.Inventory.Child.PricePerUnit = this.Price;
      this.Inventory.Child.PricePound = this.perPoundCostValue;
    }
    this.Inventory.Child.PricePerUnit = this.Inventory.PricePerUnit;
    this.Inventory.Child.PricePound = this.Inventory.PricePound;
    this.Inventory.Child.DifferentialPerUnit = this.Inventory.DifferentialPerUnit;
    this.Inventory.Child.DifferentialPerPound = this.Inventory.DifferentialPerPound;
    this.Inventory.Child.PriceDescriptorID = this.Inventory.PriceDescriptorID;

    // Vendor-specific defaults
    this.decafSeaforth = this.Inventory.Child.DecaffeinatorID == 6815 ? 800 : 0;
    this.decafLossValuePercent = this.Inventory.Child.DecaffeinatorID == 6815 ? 6.25 : 5.00;

    // Pre/post-loss weights
    const preLossNet = Math.max(1, Number(this.netWeight || 0));
    this.totalDecafNetWeightFromLoss = preLossNet * ((this.decafLossValuePercent || 0) / 100);
    this.totalDecafNetWeight = preLossNet - this.totalDecafNetWeightFromLoss;

    // Per-bag child charges use child bags
    this.decafStripping = (this.decafStrippingPerBag || 0) * DecafInventory.BagsOrdered;
    this.decafStorage = (this.decafStoragePerBag || 0) * DecafInventory.BagsOrdered;
    this.decafWeighing = (this.decafWeighingPerBag || 0) * DecafInventory.BagsOrdered;

    // Base per-lb for loss/financing: EXCLUDE commissions to avoid leaking them into tolling
    const baseFinalInclComm = this.cost

    // Core decaf costs
    this.DecafCostTotal = (this.DecafCostPerPound || 0) * preLossNet;
    this.decafLossValue = (baseFinalInclComm * preLossNet) * ((this.decafLossValuePercent || 0) / 100);
    this.decafFinancing = ((baseFinalInclComm * preLossNet) * ((this.decafFinancingPercent || 0) / 100)) / 365 * 120;
    this.decafTolling =
      (this.DecafCostTotal || 0) +
      (this.decafSeaforth || 0) +
      (this.decafStripping || 0) +
      (this.decafStorage || 0) +
      (this.decafWeighing || 0) +
      (this.decafLossValue || 0) +
      (this.decafFinancing || 0) +
      (this.decafTrucking || 0)

    // Use post-loss denominator (matches your calc form)
    const denom = Math.max(1, Number(this.totalDecafNetWeight || 0));
    this.decafTollingPerPound = this.decafTolling / this.netWeight;

    // Final rollups (don't add commissions twice—baseFinalInclComm already had them)
    this.totalOrderWithDecafCost = this.decafTollingPerPound + baseFinalInclComm + (this.decafBuffer || 0);

    this.Inventory.Child.Tolling = this.decafTollingPerPound;
    this.tollingCost = this.decafTollingPerPound;

    this.Inventory.Child.FinalWarehouseInCost =
      (this.Inventory.PricePound || 0) + this.Inventory.Child.Tolling + Number(this.TotalCommissions || 0);

    this.Inventory.Child.FinalWarehouseInDifferential =
      (this.Inventory.DifferentialPerPound || 0) + this.Inventory.Child.Tolling + Number(this.TotalCommissions || 0);

    this.Inventory.Child.EstimatedCost =
      (this.Inventory.Child.FinalWarehouseInDifferential || 0) + (this.Inventory.FixationCurrentLevel || 0);
    this.Inventory.FinalWarehouseInCostTemp = this.Inventory.BuyingTermID === 2 ? null:  baseFinalInclComm;
    this.Inventory.FinalWarehouseInCost = null;
    this.Inventory.FinalWarehouseInDifferentialTemp = this.Inventory.BuyingTermID === 1 ? null : this.Inventory.FinalWarehouseInDifferential;
    this.Inventory.FinalWarehouseInDifferential = null;
    this.Inventory.EstimatedCostTemp = this.Inventory.BuyingTermID === 1 ? null : this.Inventory.EstimatedCost;
    this.Inventory.EstimatedCost = null;
    this.enforceDecafHandling(this.tollingCost);
  }

  // ===========================================================================
  // UI helpers & Save
  // ===========================================================================
  private num(x: any): number {
    // Treat '', null, undefined, NaN as 0; otherwise parse to number
    if (x === '' || x === null || x === undefined) return 0;
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  }

  UpdateTotalCommissions(): number {
    const total =
      this.num(this.Inventory.SupplierCommission) +
      this.num(this.Inventory.RainforestCommission) +
      this.num(this.Inventory.OrganicCommission) +
      this.num(this.Inventory.IntermediaryCommission) +
      this.num(this.Inventory.CommissionOtherLevel);

    this.TotalCommissions = total;        // keep it as a number
    return total;
  }


  invalidFieldLabels(form: NgForm): string {
    const labels: Record<string, string> = {
      Product: 'Coffee Type', FixationCurrentLevel: 'Price Base At Purchase',
      FutureCompositeID: 'Futures Month', fixedby: 'Price To Be Fixed By',
      hedged: 'Internally Hedged', BuyingTermID: 'Fixed Or Differential',
      PriceDescriptorID: 'Price Descriptor', FinalDestinationWarehouseID: 'Destination Warehouse',
      ETDMonth: 'ETD Month', ETDYear: 'ETD Year', price: 'Price', diff: 'Differential'
    };
    return Object.entries(form.controls).filter(([, control]) => control.invalid)
      .map(([name]) => labels[name] ?? name.replace(/([a-z])([A-Z])/g, '$1 $2')).join(', ');
  }

  LeaveInventoryForm() {
    if (this.saving || !confirm("Discard changes to this inventory line?")) return;
    if (this.Updating) {
      this.Inventory = cloneDraft(this.SelectedInventory)
    }
    this.onLeaveInventoryForm.emit();
  }
  async SaveInventoryForm() {
    if (this.saving || this.loading) return;
    if (!(Number(this.Inventory.BagsOrdered) > 0) || !(Number(this.Inventory.NetWeight) > 0)) { this.calculationError = 'Enter a positive bag quantity and weight.'; return; }
    clearTimeout((this as any)._costRecalcTimer);
    clearTimeout((this as any)._decafTimer);
    clearTimeout((this as any)._fixationTimer);
    await this.recalculateCosts();
    if (this.Inventory.Child) await this.recalculateDecafCosts();
    if (this.calculationError) return;
    // --- your existing handling/guard & Fair Trade checks here ---

    // Price label (unchanged)
    this.Inventory.PriceLabel = this.Inventory.BuyingTermID == 2
      ? ((this.Inventory.DifferentialPerUnit >= 0 ? '+' + this.Inventory.DifferentialPerUnit : this.Inventory.DifferentialPerUnit) + ' ' +
        this.InventoryData.PriceDescriptors.find((pd: any) => pd.PriceDescriptorID === this.Inventory.PriceDescriptorID).PriceDescriptorName)
      : (this.Inventory.PricePerUnit + ' ' +
        this.InventoryData.PriceDescriptors.find((pd: any) => pd.PriceDescriptorID === this.Inventory.PriceDescriptorID).PriceDescriptorName);

    const isUpdate = !!this.Inventory.InventoryID;
    let hasChild = !!this.Inventory?.Child;
    const childHasId = !!this.Inventory?.Child?.InventoryID;
    const decafId = this.getDecafIdForDestination(this.Inventory.Destination);

    // --- Decaf rules ----------------------------------------------------------
    if (isUpdate && childHasId) {
      // We came in viewing an existing PO with a decaf child

      if (decafId) {
        // Still a decaf destination (10 or 15): swap vendor and update the child
        delete this.SelectedInventory.ChildInventory;
        delete this.Inventory.ChildInventory;
        this.Inventory.Child.DecaffeinatorID = decafId;
        this.Inventory.DecafInventory = this.buildChildPayload(this.Inventory, this.Inventory.Child);
        delete (this.Inventory as any).__deleteChild;
      }

    } else {
      // Create flow, or parent had no saved child yet
      if (hasChild) {
        // If destination is decaf, ensure DecaffeinatorID aligns
        if (decafId) this.Inventory.Child.DecaffeinatorID = decafId;

        // ALWAYS send a child payload if a child exists (prevents accidental appends)
        this.Inventory.DecafInventory = this.buildChildPayload(this.Inventory, this.Inventory.Child);
        delete (this.Inventory as any).__deleteChild;
      } else {
        // No child → nothing special to send
        this.recalculateCosts();
        this.Inventory.FOBtoExWarehouseValueTemp = this.FOBtoExWarehouseValueTemp;
        delete (this.Inventory as any).__deleteChild;
        delete this.Inventory.DecafInventory;
      }
    }
    if (this.OfferSample) {
      this.Inventory.OfferSampleID = this.OfferSample.OfferSampleID;
    }
    console.log('Final Inventory payload:', this.Inventory);
    // --------------------------------------------------------------------------

    // Emit update for existing rows; save for new
    if (isUpdate) {
      this.onUpdateInventoryForm.emit(this.Inventory);
    } else {
      this.onSaveInventoryForm.emit(this.Inventory);
    }
  }


  private enforceDecafHandling(toll?: number) {
    const child = this.Inventory?.Child || this.Inventory?.DecafInventory;
    const hasDecafChild = !!child?.DecaffeinatorID;

    if (!hasDecafChild) return;

    // Parent handling forced to 0.00
    this._fobToExWhseOverride = 0.00;                 // overrides getter computation
    this.Inventory.FOBtoExWarehouseValueTemp = 0.00;  // persist in model
    this.Inventory.ExWhseCost = 0.00;
    this.Inventory.FinalWarehouseInCost = null;
    this.Inventory.FinalWarehouseInDifferential = null;

    const tolling = Number(toll ?? this.tollingCost ?? 0);

    // Put tolling on the CHILD in the right field
    if (this.Inventory.ShipmentTypeID === 1 && this.Inventory.ShippingTerm !== 4) {
      child.FOBtoExWarehouseValueTemp = tolling;
      child.ExWhseCost = null;
    } else {
      child.ExWhseCost = tolling;
      child.FOBtoExWarehouseValueTemp = null;
    }

  }
  private buildCalcData(decaf: boolean): CalcData {
    const common: CalcData = {
      Decaf: decaf,
      WorstCase: !!this.Inventory.WorstCase,
      TariffInPrice: this.Inventory.TariffInPrice,
      TariffAmount: this.Inventory.TariffAmount,

      ShippingTerm: this.Inventory.ShippingTerm,
      ShipmentTypeID: this.Inventory.ShipmentTypeID,
      FOBtoExWarehouseValueTemp: this.FOBtoExWarehouseValueTemp,
      ExWhseCost: this.Inventory.ExWhseCost ?? null,

      // weights & price base
      netWeight: this.netWeight,
      grossWeight: this.grossWeight,
      totalBags: this.totalBags,
      cost: this.cost,

      // port / fees
      steamShipRate: this.steamShipRate,
      marineInsurance: this.marineInsurance,
      dutyAndEntry: this.dutyAndEntry,
      drayage: this.drayage,
      stripping: this.stripping,
      storage: this.storage,
      weighing: this.weighing,
      financing: this.financing,
      fot: this.fot,
      sampleCharge: this.sampleCharge,
      rfa: this.rfa,
      rateBuffer: this.rateBuffer,
      finalCostWith: this.finalCostWith,
      DaysToFinance: this.DaysToFinance,

      // derived (good to show as read-only)
      totalCost: this.totalCost,
      steamShipRatePerPound: this.steamShipRatePerPound,
      marineInsurancePerPound: this.marineInsurancePerPound,
      dutyAndEntryPerPound: this.dutyAndEntryPerPound,
      drayagePerPound: this.drayagePerPound,
      strippingPerPound: this.strippingPerPound,
      storagePerPound: this.storagePerPound,
      weighingPerPound: this.weighingPerPound,
      financingPerPound: this.financingPerPound,
      fotPerPound: this.fotPerPound,
      sampleChargePerPound: this.sampleChargePerPound,
      rfaPerPound: this.rfaPerPound,
      totalEstimate: this.totalEstimate,
    };

    if (!decaf) return common;

    // --- Decaf side ---
    const child = this.Inventory?.Child ?? {};
    return {
      ...common,
      DecaffeinatorID: child.DecaffeinatorID,
      DecafCostPerPound: this.DecafCostPerPound,
      DecafCostTotal: this.DecafCostTotal,
      decafSeaforth: child.DecaffeinatorID === 6815 ? 800 : this.decafSeaforth,

      decafStripping: this.decafStripping,
      decafStrippingPerBag: this.decafStrippingPerBag,
      decafStorage: this.decafStorage,
      decafStoragePerBag: this.decafStoragePerBag,
      decafWeighing: this.decafWeighing,
      decafWeighingPerBag: this.decafWeighingPerBag,

      decafLossValue: this.decafLossValue,
      decafLossValuePercent: this.decafLossValuePercent,
      totalDecafLossValue: this.totalDecafLossValue,
      decafFinancing: this.decafFinancing,
      decafFinancingPercent: this.decafFinancingPercent,

      totalDecafNetWeight: this.totalDecafNetWeight,
      totalDecafNetWeightFromLoss: this.totalDecafNetWeightFromLoss,
      decafBuffer: this.decafBuffer,
      decafTolling: this.decafTolling,
      decafTollingPerPound: this.decafTollingPerPound,
      totalOrderWithDecafCost: this.totalOrderWithDecafCost,
      decafTrucking: this.decafTrucking,
      tollingCost: this.tollingCost,
      parentBaseFinalPerLb: Number(this.Inventory.FinalWarehouseInCostTemp ?? this.Inventory.EstimatedCostTemp ?? 0),
      parentNetWeight: Number(this.netWeight || 0),
      totalCommissions: Number(this.TotalCommissions || 0),

      // NEW: child bags (per-bag fees are based on child)
      childBagsOrdered: Number(child.BagsOrdered ?? this.Inventory.BagsOrdered ?? 0),

      // NEW: for derived child fields CalcForm emits (optional context)
      parentPricePound: this.Inventory.PricePound ?? null,
      parentDifferentialPerPound: this.Inventory.DifferentialPerPound ?? null,
      parentFixationCurrentLevel: this.Inventory.FixationCurrentLevel ?? null,
    };
  }

  toggleTotals(): void {
    this.UpdateTotalCommissions();
    if (this.showTotals) {
      this.closingTotals = true;
      this.schedule(() => {
        this.showTotals = false;
        this.closingTotals = false;
      }, 300);
    } else {
      // open non-decaf modal
      this.calcModalData = this.buildCalcData(false);
      this.showDecafTotals = false;
      this.showTotals = true;
    }
  }

  toggleDecafTotals(): void {
    this.UpdateTotalCommissions();
    if (this.showDecafTotals) {
      this.closingTotals = true;
      this.schedule(() => {
        this.showDecafTotals = false;
        this.closingTotals = false;
      }, 300);
    } else {
      // open decaf modal
      this.calcModalData = this.buildCalcData(true);
      this.showTotals = false;
      this.showDecafTotals = true;
    }
  }
  handleTotalsChange(data: CalcData) {
    if (!data.Decaf) {
      this.costOverrides = { steamShipRate: data.steamShipRate, rateBuffer: data.rateBuffer };
      this._fobToExWhseOverride = null;
      this.hasUserOverriddenFob = false;
      // editable fields in non-decaf modal
      this.steamShipRate = data.steamShipRate ?? this.steamShipRate;
      this.marineInsurance = data.marineInsurance ?? this.marineInsurance;
      this.fot = data.fot ?? this.fot;
      this.rfa = data.rfa ?? this.rfa;
      this.finalCostWith = data.finalCostWith ?? this.finalCostWith;
      this.rateBuffer = data.rateBuffer ?? this.rateBuffer;
      this.DaysToFinance = data.DaysToFinance ?? this.DaysToFinance;

      // let parent recompute everything & update Inventory fields
      this.recalculateCostsDebounced();
    } else {
      // editable fields in decaf modal
      this.DecafCostPerPound = data.DecafCostPerPound ?? this.DecafCostPerPound;
      this.decafStrippingPerBag = data.decafStrippingPerBag ?? this.decafStrippingPerBag;
      this.decafStoragePerBag = data.decafStoragePerBag ?? this.decafStoragePerBag;
      this.decafWeighingPerBag = data.decafWeighingPerBag ?? this.decafWeighingPerBag;
      this.decafBuffer = data.decafBuffer ?? this.decafBuffer;
      this.decafTrucking = data.decafTrucking ?? this.decafTrucking;

      this.recalculateDecafCostsDebounced();
    }
  }

  onCalcClose() {
    this.closingTotals = true;
    this.schedule(() => {
      this.showTotals = false;
      this.showDecafTotals = false;
      this.closingTotals = false;
      this.calcModalData = null;
    }, 300);
  }


  /**
   * Recomputes the child decaf bag count from parent/child net weights.
   * Applies the 95% yield adjustment, floors to an integer,
   * and then triggers a decaf recalc.
   */
  //UpdateDecafBags(): void {
  //  const parent = this.Inventory;
  //  const child = this.Inventory?.Child;

  //  if (!parent || !child) return;
  //  if (!parent.NetWeight || !child.NetWeight || !child.BagsOrdered) return;

  //  const rawBags = (child.BagsOrdered * parent.NetWeight) / 132; //child.NetWeight;
  //  child.BagsOrdered = Math.floor(rawBags * 0.95);

  //  // ensure downstream totals reflect the change
  //  this.recalculateDecafCostsDebounced();
  //}
  private getDecafIdForDestination(dest?: number): number | null {
    if (dest === 10) return 6815;
    if (dest === 15) return 1992;
    return null;
  }

  private buildChildPayload(parent: any, child: any) {
    const childFDW = child?.FinalDestinationWarehouseID;
    const dw = this.InventoryData?.DestinationWarehouses
      ?.find((d: any) => d.LocationID === childFDW);
    console.log(child);
    return {
      // >>> Preserve identity so the parent replaces instead of appending
      ...child,
      InventoryID:  child?.InventoryID ? (child?.InventoryID === parent.InventoryID ? null : child.InventoryID) : null,
      _tempKey: child?._tempKey,                        
      ParentInventoryID: parent?.InventoryID ?? child?.ParentInventoryID ?? null,

      // Child flags
      IsChild: true,
      readOnly: true,

      // Child-specific values
      BagsOrdered: child?.BagsOrdered ?? null,
      NetWeight: child?.NetWeight ?? null,
      InventoryTypeID: child?.InventoryTypeID ?? null,
      InventoryTypeName: child?.ProductName ?? null,
      DecaffeinatorID: child?.DecaffeinatorID ?? null,

      // Handling/tolling on the child in the right field
      FOBtoExWarehouseValueTemp:
        parent.ShipmentTypeID === 1 && parent.ShippingTerm !== 4 ? this.tollingCost : null,
      ExWhseCost:
        parent.ShipmentTypeID === 1 && parent.ShippingTerm !== 4 ? null : this.tollingCost,

      // Destination/Warehouse
      FinalDestinationWarehouseID: childFDW,
      Destination: dw?.DestinationId ?? null,
      FinalPosition: child?.FinalPosition ?? null,

      // Price/result fields (if you keep them on the child)
      FinalWarehouseInCost: child?.FinalWarehouseInCost ?? null,
      FinalWarehouseInDifferential: child?.FinalWarehouseInDifferential ?? null,

      // Optional: mirror pricing basis to child for consistency
      //PricePerUnit: parent?.PricePerUnit ?? null,
      //PricePound: parent?.PricePound ?? null,
      //DifferentialPerUnit: parent?.DifferentialPerUnit ?? null,
      //DifferentialPerPound: parent?.DifferentialPerPound ?? null,
      //PriceDescriptorID: parent?.PriceDescriptorID ?? null,
    };
  }
  getUnitsForPurchasePrice(): string {
    return this.Inventory.PriceDescriptorID ? this.InventoryData.PriceDescriptors.find((pd: any) => pd.PriceDescriptorID === this.Inventory.PriceDescriptorID)?.PriceDescriptorName ?? '' : ''
  }
}
